/**
 * Website: who visits the public site, counted from its access log.
 *
 * No script on the site and no cookie — the contact page promises that — so
 * these are the web server's own counts (the API's `site_stats`): a visitor
 * is a masked address and a browser on one day, robots that say so are left
 * out, and only pages count. Close enough to see what is working, and the
 * page says "about" rather than pretend otherwise.
 */

import { useState } from 'react';
import {
  displayLocale,
  usePlatformWebsite,
  type PlatformWebsite,
  type WebsiteShare,
} from '@restaurant/api-client';
import { Bars, Card, Empty, ErrorNotice, Loading, Segmented } from '@restaurant/ui';

import { languageName, pageName, plural, shortDay } from '../overview-words';
import { formatDay } from '../words';
import './website.css';

const RANGES = [
  { value: '7', label: '7 days' },
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
] as const;

type Range = (typeof RANGES)[number]['value'];

export function WebsitePage() {
  const [range, setRange] = useState<Range>('30');
  const stats = usePlatformWebsite(Number(range));
  const locale = displayLocale();

  return (
    <>
      <header className="pf__head">
        <div>
          <h1 className="pf__title">Website</h1>
          <p className="pf__sub">
            Visitors to onrestaurant.tech, counted from the web server&rsquo;s own log: no
            script on the pages and no cookie.
          </p>
        </div>
        <Segmented
          label="How far back"
          options={RANGES.map((r) => ({ value: r.value, label: r.label }))}
          value={range}
          onChange={setRange}
        />
      </header>

      {stats.isPending && <Loading label="Counting visitors" />}
      {stats.isError && (
        <ErrorNotice title="Could not count the visitors" message={stats.error.message} />
      )}
      {stats.data &&
        (stats.data.available ? (
          <Counts stats={stats.data} locale={locale} />
        ) : (
          <Card>
            <Empty
              title="No visitor log on this server"
              description={stats.data.reason ?? ''}
            />
          </Card>
        ))}
    </>
  );
}

function Counts({ stats, locale }: { stats: PlatformWebsite; locale: string }) {
  const busiest = stats.days.reduce<PlatformWebsite['days'][number] | null>(
    (best, d) => (best === null || d.visitors > best.visitors ? d : best),
    null,
  );
  const perVisitor = stats.visitors > 0 ? stats.views / stats.visitors : 0;
  const lateStart =
    stats.logged_since !== null && stats.logged_since > stats.since ? stats.logged_since : null;

  return (
    <>
      <dl className="pf__ov-figures">
        <div className="pf__ov-figure is-lead">
          <dt>Visitors, about</dt>
          <dd>{stats.visitors.toLocaleString(locale)}</dd>
        </div>
        <div className="pf__ov-figure">
          <dt>Pages viewed</dt>
          <dd>{stats.views.toLocaleString(locale)}</dd>
        </div>
        <div className="pf__ov-figure">
          <dt>Pages per visitor</dt>
          <dd>{perVisitor.toLocaleString(locale, { maximumFractionDigits: 1 })}</dd>
        </div>
        <div className="pf__ov-figure">
          <dt>Busiest day</dt>
          <dd className="pf__ws-day">
            {busiest && busiest.visitors > 0 ? shortDay(busiest.day, locale) : '—'}
          </dd>
        </div>
      </dl>

      <Card title="Visitors per day">
        <p className="pf__faint">
          {formatDay(stats.since, locale)} to {formatDay(stats.today, locale)}, by the UTC
          calendar.
          {lateStart &&
            ` The log starts on ${formatDay(lateStart, locale)}; days before it were not counted.`}
        </p>
        <Bars
          unit="day"
          fit
          bars={stats.days.map((d) => ({
            key: d.day,
            label: shortDay(d.day, locale),
            value: d.visitors,
            caption: `${plural(d.visitors, 'visitor', 'visitors')}, ${plural(d.views, 'page', 'pages')}`,
          }))}
        />
      </Card>

      <div className="pf__grid">
        <Card title="Pages">
          {stats.pages.length === 0 ? (
            <Empty title="No pages viewed yet" />
          ) : (
            <div className="pf__table-wrap">
              <table className="pf__table">
                <thead>
                  <tr>
                    <th>Page</th>
                    <th className="pf__num">Views</th>
                    <th className="pf__num">Visitors</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.pages.map((page) => (
                    <tr key={page.path}>
                      <td>
                        <a
                          className="pf__rowlink"
                          href={`https://onrestaurant.tech${page.path}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {pageName(page.path)}
                        </a>
                      </td>
                      <td className="pf__num">{page.views.toLocaleString(locale)}</td>
                      <td className="pf__num">{page.visitors.toLocaleString(locale)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card title="Where they came from">
          <Shares rows={stats.sources} empty="Nobody yet" />
        </Card>

        <Card title="Devices">
          <Shares rows={stats.devices} empty="Nobody yet" />
        </Card>

        <Card title="Languages">
          <Shares
            rows={stats.languages}
            empty="Nobody yet"
            name={(code) => languageName(code, locale)}
          />
        </Card>
      </div>
    </>
  );
}

/** Each row a name, a count, and its share of the whole as a bar. */
function Shares({
  rows,
  empty,
  name = (n) => n,
}: {
  rows: WebsiteShare[];
  empty: string;
  name?: (raw: string) => string;
}) {
  const total = rows.reduce((sum, row) => sum + row.visitors, 0);
  if (total === 0) return <Empty title={empty} />;
  return (
    <ul className="pf__ws-shares">
      {rows.map((row) => {
        const share = Math.round((row.visitors / total) * 100);
        return (
          <li key={row.name} className="pf__ws-share">
            <span className="pf__ws-name">{name(row.name)}</span>
            <span className="pf__ws-count">
              {row.visitors} <span className="pf__faint">· {share}%</span>
            </span>
            <span className="pf__ws-bar" aria-hidden="true">
              <span style={{ width: `${Math.max(share, 2)}%` }} />
            </span>
          </li>
        );
      })}
    </ul>
  );
}
